// src/components/Footer.js
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Phone, Globe, MessageCircle } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useDispatch, useSelector } from "react-redux";
import { fetchSettings } from "../features/settings/settingsSlice";

const Footer = () => {
  const { t } = useLanguage();
  const dispatch = useDispatch();
  const { data: settings, loading } = useSelector((state) => state.settings);

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  if (loading || !settings) return null;

  return (
    <footer
      style={{
        background: "rgb(0, 55, 32)",
        color: "white",
        padding: "4rem 1.5rem 2rem",
      }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              {settings.logo && (
                <img
                  src={settings.logo}
                  alt={settings.nama}
                  style={{
                    width: 50,
                    height: 50,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              )}
              <div>
                <h3 style={{ fontWeight: 700, fontSize: "1.25rem" }}>
                  {settings.nama}
                </h3>
              </div>
            </div>

            {settings.description && (
              <p
                style={{
                  color: "rgba(255, 255, 255, 0.8)",
                  fontSize: "0.875rem",
                  lineHeight: 1.6,
                  marginBottom: "1rem",
                }}
              >
                {settings.description}
              </p>
            )}

            {settings.address && (
              <div className="flex items-start gap-2" style={{ marginBottom: "0.5rem" }}>
                <MapPin size={16} />
                <span style={{ fontSize: "0.875rem" }}>{settings.address}</span>
              </div>
            )}

            {settings.phone && (
              <div className="flex items-start gap-2" style={{ marginBottom: "0.5rem" }}>
                <Phone size={16} />
                <a
                  href={`tel:${settings.phone}`}
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    textDecoration: "none",
                    fontSize: "0.875rem",
                  }}
                >
                  {settings.phone}
                </a>
              </div>
            )}

            {settings.email && (
              <div className="flex items-start gap-2">
                <Mail size={16} />
                <a
                  href={`mailto:${settings.email}`}
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    textDecoration: "none",
                    fontSize: "0.875rem",
                  }}
                >
                  {settings.email}
                </a>
              </div>
            )}
          </div>

          {/* Navigation Links */}
          <div>
            <h4 style={{ fontWeight: 600, fontSize: "1rem", marginBottom: "1rem" }}>
              {t.nav.home === "Home" ? "Quick Links" : "Tautan Cepat"}
            </h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {[
                { path: "/", label: t.nav.home },
                { path: "/why-us", label: t.nav.whyUs },
                { path: "/team", label: t.nav.team },
                { path: "/clients", label: t.nav.clients },
                { path: "/activities", label: t.nav.activities },
              ].map((item, i) => (
                <li key={i} style={{ marginBottom: "0.5rem" }}>
                  <Link
                    to={item.path}
                    style={{
                      color: "rgba(255, 255, 255, 0.8)",
                      textDecoration: "none",
                      fontSize: "0.875rem",
                      transition: "color 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.target.style.color = "#8FEC78")}
                    onMouseLeave={(e) =>
                      (e.target.style.color = "rgba(255, 255, 255, 0.8)")
                    }
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Social Media */}
          <div>
            <h4 style={{ fontWeight: 600, fontSize: "1rem", marginBottom: "1rem" }}>
              Media Sosial
            </h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {settings.instagram && (
                <li style={{ marginBottom: "0.5rem" }}>
                  <a
                    href={settings.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "rgba(255, 255, 255, 0.8)", textDecoration: "none" }}
                  >
                    Instagram
                  </a>
                </li>
              )}
              {settings.twitter && (
                <li style={{ marginBottom: "0.5rem" }}>
                  <a
                    href={settings.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "rgba(255, 255, 255, 0.8)", textDecoration: "none" }}
                  >
                    Twitter
                  </a>
                </li>
              )}
              {settings.youtube && (
                <li style={{ marginBottom: "0.5rem" }}>
                  <a
                    href={settings.youtube}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "rgba(255, 255, 255, 0.8)", textDecoration: "none" }}
                  >
                    YouTube
                  </a>
                </li>
              )}
              {settings.tiktok && (
                <li style={{ marginBottom: "0.5rem" }}>
                  <a
                    href={settings.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "rgba(255, 255, 255, 0.8)", textDecoration: "none" }}
                  >
                    TikTok
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Additional Contact */}
          <div>
            <h4 style={{ fontWeight: 600, fontSize: "1rem", marginBottom: "1rem" }}>
              Hubungi Kami
            </h4>

            {settings.whatsapp && (
              <div
                className="flex items-center gap-2"
                style={{ marginBottom: "0.75rem" }}
              >
                <MessageCircle size={16} />
                <a
                  href={`https://wa.me/${settings.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    textDecoration: "none",
                    fontSize: "0.875rem",
                  }}
                >
                  WhatsApp
                </a>
              </div>
            )}

            {settings.google_map && (
              <div className="flex items-center gap-2">
                <Globe size={16} />
                <a
                  href={settings.google_map}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "rgba(255, 255, 255, 0.8)",
                    textDecoration: "none",
                    fontSize: "0.875rem",
                  }}
                >
                  Lihat di Google Maps
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Copyright */}
        <div
          style={{
            borderTop: "1px solid rgba(255, 255, 255, 0.2)",
            paddingTop: "2rem",
            textAlign: "center",
          }}
        >
          <p
            style={{
              fontSize: "0.875rem",
              color: "rgba(255, 255, 255, 0.6)",
              margin: 0,
            }}
          >
            © {new Date().getFullYear()} {settings.nama}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
